import { Component, Input, Output, EventEmitter, ElementRef, forwardRef, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
    host: {
        '(document:click)': 'onClick($event)',
    },
    selector: 'selector',
    templateUrl: './selector.component.html',
    styleUrls: ['./selector.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => Selector),
            multi: true
        }
    ],
    standalone: false
})
export class Selector implements ControlValueAccessor, OnInit {
    private searchSubject: Subject<any> = new Subject();

    public show: boolean | undefined;

    public filterData: any;

    public totalPages: number = 0;
    public activePage: number = 0;

    public selectedValue: any;
    public selectedItem: any;
    public checkedItem: any = [];


    constructor(private eref: ElementRef) {}
    ngOnInit(): void {
        if (this.options && this.options.length === 0) {
            this.checkAll = false;
            this.selectedItem = undefined;
            this.selectedValue = undefined;
            this.activePage = 0;
            this.totalPages = 0;
          }

          this.searchSubject.pipe(
            debounceTime(600)
          ).subscribe(value => {
            if (this.onLazyLoad) {
              this.onLazyLoad.emit({
                originalEvent: value,
                activePage: this.activePage,
                filterData: value
              });
            }
          });
        //   this.onChange(this.value);
        //   this.onTouched();
    }

    @Input() disabled: boolean = false;
    @Input() value: any;

    onChange: any = () => {};
    onTouched: any = () => {};

    localOptions: any[] = [];
    @Input() 
    get options() : any[] {
      return this.localOptions;
    }
    set options(values:  any[]){
      this.localOptions = values;
      if (this.localOptions?.length == 1 && this.activePage == 0 && !this.filterData){
        this.writeValue(this.localOptions[0]);
        this.onChange(this.selectedItem);
        this.onSelect.emit({
        selected: this.selectedItem
        });
      }
    }

    @Input() rowsPerPageOption: number = 5;

    localTotalRecords: number = 10;
    @Input() get totalRecords(): number {
        return this.localTotalRecords;
    }

    set totalRecords(val: number) {
      this.localTotalRecords = val;

      const rawPageCount = Math.trunc(this.localTotalRecords / this.rowsPerPageOption);
      const hasPartialPage = this.localTotalRecords % this.rowsPerPageOption > 0;

      this.totalPages = rawPageCount + (hasPartialPage ? 1 : 0);

      // ensure at least one page is shown
      if (this.totalPages === 0) {
        this.totalPages = 1;
      }

      this.activePage = 0;

    }

    @Input() placeholder: string | undefined;

    @Input() optionLabel: string | undefined;

    @Input() showClear: boolean = false;

    @Output() onSelect: EventEmitter<any> = new EventEmitter();

    @Output() onLazyLoad: EventEmitter<any> = new EventEmitter();

    @Input() isMultiselect: boolean = false;

    @Input('data-testid') dataTestId: string = 'selector-input';

    @Input('formControlName') formControlName: string = '';

    public checkAll: boolean = false;


    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    writeValue(value: any) {
      // Only update if necessary
      if (value !== this.selectedItem) {
        this.value = value;
        if (value) {
          this.selectedItem = Array.isArray(value) ? [...value] : { ...value };
          this.setSelectedValueDisplay();
        } else {
          this.selectedItem = undefined;
          this.selectedValue = undefined;
        }
      }

      // sync ngModel with selectedItem for checkbox selection
      if (this.selectedItem && this.isMultiselect) {
        this.selectedItem = Array.isArray(this.selectedItem) ? this.selectedItem : [this.selectedItem];
      }
    }

    hasRecords(){
      return this.options != null && this.options.length > 0;
    }

    onClick(event: any) {
        if (!this.eref.nativeElement.contains(event.target))
            this.show = false;
    }

    onClearClick(event: any) {
      this.selectedItem = undefined; // Selection
      this.selectedValue = undefined; // Display value
      this.show = false;

      this.value = this.selectedItem; // Form value

      // Notify the form about the cleanup
      this.onChange(this.selectedItem);

      this.onSelect.emit({
        originalEvent: event,
        selected: undefined
      });
    }

    onSelectClick(event: any) {
      // multiselect ensure selectedItem is always an array
      if (this.isMultiselect) {
        if (!Array.isArray(this.selectedItem)) {
          this.selectedItem = [this.selectedItem];
        }
        this.show = true;
      } else {
        this.show = false;
      }

      this.setSelectedValueDisplay();
      this.onChange(this.selectedItem); // Update form control value
      this.onSelect.emit({
        originalEvent: event,
        selected: this.selectedItem // Properly emit selected value
      });
    }

    setSelectedValueDisplay() {
      if (this.selectedItem) {
        if (Array.isArray(this.selectedItem)) {
          this.selectedValue = this.selectedItem.map(item => item[this.optionLabel!]).join(', ');
        } else {
          this.selectedValue = this.selectedItem[this.optionLabel!];
        }
      } else {
        this.selectedValue = undefined;
      }
    }

    onChangeFilter(event: any) {
      this.activePage = 0;
      this.searchSubject.next(event);
    }

    onEnter(event: Event) {
      const keyboardEvent = event as KeyboardEvent;
      keyboardEvent.preventDefault();

      this.onChangeFilter(this.filterData);
    }

    onPreviousPage(event: any) {
        if (this.activePage == 0)
            return;

        this.activePage = this.activePage - 1;

        if (this.onLazyLoad) {
            this.onLazyLoad.emit({
                originalEvent: event,
                activePage: this.activePage,
                filterData: this.filterData
            });
        }
    }

    onNextPage(event: any) {
        if (this.activePage >= this.totalPages - 1) return;

        this.activePage = this.activePage + 1;

        if (this.onLazyLoad) {
            this.onLazyLoad.emit({
                originalEvent: event,
                activePage: this.activePage,
                filterData: this.filterData
            });
        }
    }

    onMultiselectChange(event: any) {
      this.selectedItem = [];
      this.selectedValue = undefined;

      if (this.checkAll === true) {
        this.options.forEach((item, index) => {
          this.selectedItem.push(item);
          this.checkedItem.push(item);

          if (index === 0)
            this.selectedValue = item[this.optionLabel!];
          else
            this.selectedValue = this.selectedValue + ', ' + item[this.optionLabel!];
        });
      }
    }
}
